import * as R from 'ramda'

const labelFormatter = (format, dataLabel, optionLabel, decimals) => {
    let formatPreString = ''
    let formatPostSring = ''
    if (format && format !== 'numeric') {
        formatPreString = format !== '%' ? format : ''
        formatPostSring = format === '%' ? format : ''
    }

    if (dataLabel) {
        return (params) => `${formatPreString}${params.value.toFixed(decimals)}${formatPostSring}`
    }
    if (optionLabel) {
        return (params) => `${params.name}: ${formatPreString}${params.value.toFixed(decimals)}${formatPostSring}`
    }

    return (params) => `${params.name}`
}

const pieRenderSettings = (widget, dashStore) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const compact = R.pathOr(Number(R.pathOr(2, ['width'], widget)) < 4 ? 'vertical' : 'horizontal',
        ['widgetRenderSettings', 'orientation'],
        widget) === 'vertical'

    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const defaultRadius = widget.chartType === 'donut' ? ['30%', '50%'] : '50%'
    const noLegendRadius = widget.chartType === 'donut' ? ['45%', '65%'] : '65%'
    const colors = R.pathOr(undefined, ['widgetRenderSettings', 'colors'], widget)

    const layout = compact ? [
        { path: ['tooltip', 'position'], value: ['20%', '40%'] },
        {
            path: ['series', 0, 'center'],
            value: legendSetting ? ['50%', '30%'] : ['50%', '50%'],
        },
        {
            path: ['series', 0, 'radius'],
            value: legendSetting ? defaultRadius : noLegendRadius,
        },
        {
            path: ['legend', 'bottom'],
            value: 50,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
    ] : [
        { path: ['tooltip', 'position'], value: ['20%', '40%'] },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
    ]

    layoutEdits.push(...layout)

    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const showOptionLabels = R.pathOr(false, ['widgetRenderSettings', 'showOptionLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)

    if (showDataLabels || showOptionLabels) {
        const formatter = labelFormatter(
            format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
            showDataLabels,
            showOptionLabels,
            decimals,
        )

        layoutEdits.push(
            {
                path: ['series', 0, 'label'],
                value: {
                    show: true,
                    overflow: 'break',
                    position: R.pathOr('inside', ['widgetRenderSettings', 'labelPosition'], widget),
                    fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                    fontWeight: 700,
                    lineHeight: 18,
                    fontSize: 12,
                    width: 100,
                    formatter,
                },
            },
            {
                path: ['series', 0, 'labelLayout', 'hideOverlap'],
                value: R.pathOr(false, ['widgetRenderSettings', 'hideOverlapLabels'], widget),
            },
        )
    }
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)

    if (showTitle) {
        layoutEdits.push(
            {
                path: ['title'],
                value: {
                    text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                    left: 'left',
                    show: true,
                    textStyle: {
                        fontWeight: 'bold',
                        fontSize: 16,
                        color: '#102E4A',
                    },
                },
            },
        )
    }

    if (colors) {
        layoutEdits.push({
            path: ['color'],
            value: colors,
        })
    }

    return { layoutEdit: layoutEdits }
}

const columnRenderSettings = (widget, dashStore, xAxisLabels, fData) => {
    const {
        appStore, getChartScope, oId, benchmarkPeriod, benchmarkPeriods,
    } = dashStore
    const { langcode } = appStore
    const { chartType } = widget
    const layoutEdits = []
    const isBar = chartType === 'bar' || chartType === 'stackedBar'
    const isLine = chartType === 'line'
    const isStacked = chartType.includes('stacked')
    const yaxis = isBar ? 'xAxis' : 'yAxis'
    const xaxis = isBar ? 'yAxis' : 'xAxis'
    const rotateX = R.pathOr(!isBar && xAxisLabels.length > 8, ['widgetRenderSettings', 'rotateXAxisLabels'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        false,
        decimals,
    )
    const showYAxis = R.pathOr(true, ['widgetRenderSettings', 'showYAxis'], widget)
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const axisOverflow = rotateX ? 'truncate' : 'break'
    const boundaryGap = R.pathOr(true, ['widgetRenderSettings', 'boundaryGap'], widget)
    const swapSeriesXAxis = R.pathOr(false, ['widgetRenderSettings', 'swapSeriesXAxis'], widget)
    const colors = R.pathOr(undefined, ['widgetRenderSettings', 'colors'], widget)
    const benchmarkDuration = benchmarkPeriods
        .find((bp) => bp.value === benchmarkPeriod).period.duration
    const groupByTags = !!widget.customTag

    if (isBar) {
        layoutEdits.push({
            path: ['yAxis', 'axisLabel', 'fontSize'],
            value: 12,
        })
    }

    layoutEdits.push(
        {
            path: ['xAxis', 'axisLabel', 'fontSize'],
            value: 10,
        },
        {
            path: ['xAxis', 'axisLabel', 'color'],
            value: 'black',
        },
        {
            path: [xaxis, 'axisLabel', 'interval'],
            value: 0,
        },
        {
            path: [xaxis, 'axisLabel', 'overflow'],
            value: isBar ? undefined : axisOverflow,
        },
        {
            path: [xaxis, 'axisLabel', 'width'],
            value: rotateX ? 140 : 100,
        },
        {
            path: [xaxis, 'axisLabel', 'rotate'],
            value: rotateX ? 45 : 0,
        },
        {
            path: [xaxis, 'boundaryGap'],
            value: boundaryGap,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['legend', 'type'],
            value: 'scroll',
        },
        {
            path: ['legend', 'itemGap'],
            value: 25,
        },
        {
            path: ['grid'],
            value: {
                containLabel: true,
                top: showTitle ? 50 : 20,
                bottom: legendSetting ? 30 : 10,
                left: rotateX ? 20 : 10,
                right: !rotateX ? 40 : 5,
            },
        },
        {
            path: [yaxis, 'axisLabel', 'show'],
            value: showYAxis,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
    )

    let markAreaData
    let markAreaSeries = 0
    let newXaxisLabels
    if (!['pct'].includes(widget.kpiType) && !isLine && !isStacked && (!swapSeriesXAxis || widget.benchmarkType === 'YOY')
    && (getChartScope(oId) !== widget.scope || widget.benchmarkType === 'YOY')
    && (!['TB', 'TB - row'].includes(widget.questionTypeCode))
    && (!(getChartScope(oId) === 'event' && widget.scope === 'product') || widget.benchmarkType === 'YOY')
    && (widget.kpiType !== 'multiQuestion')) { // do not add benchmark line for multiQuestions
        const standardData = R.clone(fData)
        const benchmarkData = []
        // get the benchmarkData as seperate series's
        fData.forEach((series) => series.data.filter((d) => !!d.benchmark || !!R.pathOr(false, [0, 'benchmark'], d)).forEach((d) => {
            benchmarkData.push(standardData[0].data.map(() => d))
        }))
        // remove the xAxis labels for the benchmarks
        newXaxisLabels = [...xAxisLabels]
        benchmarkData.forEach((d) => {
            const labelIndex = newXaxisLabels.indexOf(d[0].name)
            if (labelIndex > -1) {
                newXaxisLabels.splice(labelIndex, 1)
                standardData.forEach((sd, i) => {
                    standardData[i].data.splice(labelIndex, 1)
                })
                benchmarkData.forEach((sd, i) => {
                    benchmarkData[i].splice(labelIndex, 1)
                })
            }
            if (widget.benchmarkType === 'YOY') {
                const labelIndexYOY = newXaxisLabels.indexOf(`${benchmarkDuration} Months`)
                if (labelIndexYOY > -1) {
                    newXaxisLabels.splice(labelIndexYOY, 1)
                    standardData.forEach((sd, i) => {
                        standardData[i].data.splice(labelIndexYOY, 1)
                    })
                    benchmarkData.forEach((sd, i) => {
                        benchmarkData[i].splice(labelIndexYOY, 1)
                    })
                }
                const labelIndexYOYAll = newXaxisLabels.indexOf('All Time')
                if (labelIndexYOYAll > -1) {
                    newXaxisLabels.splice(labelIndexYOYAll, 1)
                    standardData.forEach((sd, i) => {
                        standardData[i].data.splice(labelIndexYOYAll, 1)
                    })
                    benchmarkData.forEach((sd, i) => {
                        benchmarkData[i].splice(labelIndexYOYAll, 1)
                    })
                }
            }
        })

        // if there are still x axis labels after removing all benchmarks, continue
        if (newXaxisLabels.length > 1 || (widget.benchmarkType === 'YOY' && newXaxisLabels.length > 0)) {
            // remove the 24 that is returned from the benchmark years from the legend as it's irrelevant
            if (widget.benchmarkType === 'YOY') {
                layoutEdits.push({
                    path: ['legend', 'data'],
                    value: [...benchmarkData, ...standardData].filter((d) => (
                        d.name ? d.name.substring(0, 2) !== String(benchmarkDuration) && d.name !== 'All Time' : true
                    )).map((s) => (
                        { name: s.name || R.pathOr(undefined, [0, 'name'], s) }
                    )),
                })
            }

            // add the benchmark series's as line series's (or bar if single series col/bar chart)
            const isSingleSeriesColChart = newXaxisLabels.length === 1
            const barLabelConfig = {
                show: showDataLabels,
                position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
                fontWeight: 'bold',
                fontSize: 11,
                color: 'black',
                formatter,
            }
            markAreaSeries += benchmarkData.length
            layoutEdits.push(...benchmarkData.map((bd, i) => (
                {
                    path: ['series', i],
                    value: {
                        type: isSingleSeriesColChart ? 'bar' : 'line',
                        label: {
                            show: false,
                            ...(isSingleSeriesColChart ? barLabelConfig : {}),
                        },
                        emphasis: {
                            focus: 'series',
                        },
                        stack: undefined,
                        data: bd,
                        name: R.pathOr(undefined, [0, 'name'], bd),
                    },
                }
            )))

            // add back the standard data complete obj as normal series's with the benchmark xaxis's removed
            const yoyStandardData = standardData.filter((s) => (
                s.name
                    ? s.name.substring(0, 2) !== String(benchmarkDuration) && s.name !== 'All Time'
                    : true && !R.pathOr(s.benchmark, ['data', 0, 'benchmark'], s)
            ))
            const useData = widget.benchmarkType === 'YOY' ? yoyStandardData : standardData
            layoutEdits.push(...useData.map((d, i) => ({
                path: ['series', i + benchmarkData.length],
                value: {
                    data: d.data,
                    name: d.name,
                    type: 'bar',
                    label: barLabelConfig,
                    emphasis: {
                        focus: 'series',
                    },
                    stack: undefined,
                    markArea: i === useData.length - 1 && !!markAreaData ? {
                        label: {
                            show: true,
                            position: 'top',
                            color: '#333',
                            fontWeight: 'bold',
                        },
                        tooltip: { show: false },
                        data: markAreaData,
                    } : undefined,
                },
            })))

            // update the xAxis labels
            layoutEdits.push({
                path: [xaxis, 'data'],
                value: newXaxisLabels,
            })
        }
    }

    if (!R.isNil(widget.min)) {
        layoutEdits.push({
            path: [yaxis, 'min'],
            value: widget.min,
        })
    }

    if (!R.isNil(widget.max)) {
        layoutEdits.push({
            path: [yaxis, 'max'],
            value: widget.max,
        })
    }

    const newSeries = {
        type: isLine ? 'line' : 'bar',
        label: {
            show: showDataLabels,
            overflow: 'break',
            position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
            fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
            fontWeight: 700,
            lineHeight: 18,
            fontSize: 12,
            formatter,
        },
        emphasis: {
            focus: 'series',
        },
        stack: isStacked ? 'data' : undefined,
        data: [],
    }

    if (colors) {
        layoutEdits.push({
            path: ['color'],
            value: colors,
        })
    }
    const finalxAxisLabels = newXaxisLabels || xAxisLabels
    if (groupByTags
        && (
            (['avg', 'nps'].includes(widget.kpiType) && (!swapSeriesXAxis))
            || (
                !['avg', 'nps'].includes(widget.kpiType) && widget.nonQuestionType !== 'KPI'
                && ((!!swapSeriesXAxis && !isStacked) || (isStacked && !swapSeriesXAxis))
            )
            || (
                widget.nonQuestionType === 'KPI' && !widget.isMatrixKpi
                && R.pathOr('', ['dataDetails', 'type'], widget) === 'avg' && !swapSeriesXAxis
            )
        )
    ) {
        let previousGroup = ''
        let grey = true
        markAreaData = finalxAxisLabels.reduce((markAreaD, xAxisItem, i) => {
            const tagOptionName = xAxisItem.split(' - ').pop()
            if (tagOptionName !== previousGroup) {
                grey = !grey
                previousGroup = tagOptionName
                const newElement = [
                    {
                        name: tagOptionName,
                        xAxis: finalxAxisLabels[i],
                        itemStyle: { color: grey ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0)' },
                    },
                    { xAxis: finalxAxisLabels[i] },
                ]
                return [...markAreaD, newElement]
            }
            let lastElement = markAreaD.pop()
            lastElement = [lastElement[0], { xAxis: finalxAxisLabels[i] }]
            previousGroup = tagOptionName
            return [...markAreaD, lastElement]
        }, [])

        layoutEdits.push({
            path: ['series', markAreaSeries, 'markArea'],
            value: {
                label: {
                    show: true,
                    position: 'top',
                    color: '#333',
                    fontWeight: 'bold',
                },
                tooltip: { show: false },
                data: markAreaData,
            },
        })
    }

    return { layoutEdit: layoutEdits, defaultSeries: newSeries, finalxAxisLabels }
}

const radarRenderSettings = (widget, dashStore, xAxisLabels, fData) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const areaFill = R.pathOr(false, ['widgetRenderSettings', 'areaFill'], widget)
    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        false,
        decimals,
    )

    const layout = [

        {
            path: ['legend', 'bottom'],
            value: 10,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
        {
            path: ['radar', 'axisLabel'],
            value: {
                show: showDataLabels,
                formatter,
            },
        },
    ]

    if (!legendSetting) {
        layoutEdits.push(
            {
                path: ['radar', 'radius'],
                value: '75%',
            },
            {
                path: ['radar', 'center'],
                value: ['50%', '55%'],
            },
        )
    }

    if (areaFill) {
        layoutEdits.push(...fData.map((d, i) => (
            {
                path: ['series', i, 'areaStyle'],
                value: {},
            }
        )))
    }

    layoutEdits.push(...layout)

    return { layoutEdit: layoutEdits }
}

const treemapRenderSettings = (widget, dashStore) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []

    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const showOptionLabels = R.pathOr(true, ['widgetRenderSettings', 'showOptionLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        showOptionLabels,
        decimals,
    )
    layoutEdits.push(
        { path: ['series', 0, 'roam'], value: false },
        { path: ['series', 0, 'nodeClick'], value: false },
        {
            path: ['series', 0, 'label'],
            value: {
                show: showDataLabels || showOptionLabels,
                position: 'insideBottomLeft',
                fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                fontWeight: 700,
                fontSize: 12,
                formatter,
            },
        },
    )
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)

    if (showTitle) {
        layoutEdits.push(
            {
                path: ['title'],
                value: {
                    text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                    left: 'left',
                    show: true,
                    textStyle: {
                        fontWeight: 'bold',
                        fontSize: 16,
                        color: '#102E4A',
                    },
                },
            },
        )
    }

    return { layoutEdit: layoutEdits }
}

const quadrantAnalysisRenderSettings = (widget, dashStore, originalxAxisLabels) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const showLabels = R.pathOr(false, ['widgetRenderSettings', 'showLabels'], widget)
    const xAxisLabels = { ...originalxAxisLabels }
    const { surveyType } = widget

    if (surveyType === 'EM') {
        xAxisLabels.x = R.or(xAxisLabels.x, '').split(' - ').slice(0, -1).join(' - ')
        xAxisLabels.y = R.or(xAxisLabels.y, '').split(' - ').slice(0, -1).join(' - ')
    }

    const layout = [
        {
            path: ['toolbox', 'feature', 'dataZoom', 'icon'],
            value: {
                zoom: 'path://M21.375,14h-4.75V9.25a1.188,1.188,0,0,0-2.375,0V14H9.5a1.188,1.188,0,0,0,0,2.375h4.75v4.75a1.188,1.188,0,0,0,2.375,0v-4.75h4.75a1.188,1.188,0,0,0,0-2.375ZM37.651,35.724l-10.5-10.5A15.426,15.426,0,1,0,25.472,26.9l10.5,10.5a1.2,1.2,0,0,0,.839.345,1.187,1.187,0,0,0,.839-2.026ZM15.438,28.25A13.063,13.063,0,1,1,28.5,15.188,13.039,13.039,0,0,1,15.438,28.25Z',
                back: 'path://M21.375,14H9.5a1.188,1.188,0,0,0,0,2.375H21.375a1.188,1.188,0,0,0,0-2.375ZM37.651,35.724l-10.5-10.5A15.426,15.426,0,1,0,25.472,26.9l10.5,10.5a1.2,1.2,0,0,0,.839.345,1.187,1.187,0,0,0,.839-2.026ZM15.438,28.25A13.063,13.063,0,1,1,28.5,15.188,13.039,13.039,0,0,1,15.438,28.25Z',
            },
        },
        { path: ['grid', 'bottom'], value: '20%' }, { path: ['grid', 'left'], value: '7%' }, { path: ['xAxis', 'name'], value: xAxisLabels.x },
        { path: ['xAxis', 'nameLocation'], value: 'middle' },
        { path: ['xAxis', 'nameGap'], value: 27 }, { path: ['yAxis', 'name'], value: xAxisLabels.y },
        { path: ['xAxis', 'nameTextStyle'], value: { fontSize: 14 } },
        { path: ['yAxis', 'nameGap'], value: 80 },
        { path: ['yAxis', 'nameTextStyle'], value: { fontSize: 14 } }, { path: ['yAxis', 'nameLocation'], value: 'middle' },
        {
            path: ['legend', 'bottom'],
            value: 10,
        },
        {
            path: ['legend', 'type'],
            value: 'scroll',
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['yAxis', 'scale'],
            value: true,
        },
        {
            path: ['xAxis', 'scale'],
            value: true,
        },
        {
            path: ['yAxis', 'max'],
            value: null,
        },
        {
            path: ['xAxis', 'max'],
            value: null,
        },
        {
            path: ['yAxis', 'minInterval'],
            value: 1,
        },
        {
            path: ['xAxis', 'minInterval'],
            value: 1,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
    ]

    if (!showLabels) {
        layout.push({ path: ['grid', 'right'], value: '5%' })
    }

    layoutEdits.push(...layout)

    return { layoutEdit: layoutEdits }
}

const gaugeRenderSettings = (widget, dashStore, xAxisLabels, data) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const {
        chartType, percentiles, max, kpiType,
    } = widget
    const { benchmarkData, sortedData } = data
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const chartCenterData = Number(R.pathOr(
        R.pathOr(undefined, ['sortedData', 0, 'data', 0, 'value'], data),
        ['sortedData', 0, 'value'],
        data,
    ))
    const mainDataYear = R.pathOr(0, ['sortedData', 0, 'year'], data)

    const centerText = (mainDataYear
        ? (
            `${mainDataYear < 1900 ? 'Last' : 'Year:'} ${mainDataYear} ${mainDataYear < 1900 ? 'months' : ''}`
        )
        : '')

    const layout = [
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
        {
            path: ['series', 0, 'detail', 'rich', 'a', 'fontSize'],
            value: decimals > 0 ? 50 : 75,
        },
    ]

    layoutEdits.push(...layout)

    let yPercentage = 100
    benchmarkData.forEach((d, i) => {
        // The index + 1 in the path is becuase the first series is always the main data
        let icon = '─'
        let iconColor = '#FEB200'
        if (d.value !== sortedData[0].value) {
            icon = sortedData[0].value > d.value ? '↗' : '↘'
            iconColor = sortedData[0].value > d.value ? '#16D2C6' : '#F46957'
        }
        const isEven = i % 2 === 0
        layoutEdits.push(
            {
                path: ['series', 0, 'data', i + 1],
                value: {
                    itemStyle: { opacity: 0 },
                    detail: {
                        offsetCenter: [isEven ? '-85%' : '85%', `${yPercentage}%`],
                        formatter: `${d.benchmarkName || d.year}: ${d.value} {c|${icon}}`,
                        rich: {
                            c: {
                                color: iconColor,
                                fontSize: 11,
                                fontWeight: 700,
                            },
                        },
                        fontSize: 11,
                        width: 120,
                        lineHeight: 14,
                        color: 'black',
                        backgroundColor: '#F7F8FA',
                        borderRadius: 3,
                        fontWeight: 600,
                    },
                },
            },
        )
        if (!isEven) yPercentage += 40
    })

    if (percentiles) {
        const maxValue = kpiType === 'nps' ? 200 : max
        const additionalPercentileVal = kpiType === 'nps' ? 100 : 0
        const axisLineColors = []
        axisLineColors.push(...[
            [(percentiles.s0 + additionalPercentileVal) / maxValue, '#bdbfbe'],
            [(percentiles.s20 + additionalPercentileVal) / maxValue, '#D9314B'],
            [(percentiles.s40 + additionalPercentileVal) / maxValue, '#FEB200'],
            [(percentiles.s60 + additionalPercentileVal) / maxValue, '#FFDA10'],
            [(percentiles.s80 + additionalPercentileVal) / maxValue, '#97D100'],
            [(percentiles.s100 + additionalPercentileVal) / maxValue, '#16CCC1'],
            [1, '#bdbfbe'],
        ])

        layoutEdits.push({
            path: ['series', 0, 'axisLine', 'lineStyle', 'color'],
            value: axisLineColors,
        })
    }

    if (isNaN(chartCenterData)) {
        layoutEdits.push(...[{
            path: ['series', 0, 'detail', 'formatter'],
            value: `{a|No data}\n{a|available}\n{b|${centerText}}`,
        },
        {
            path: ['series', 0, 'detail', 'width'],
            value: 30,
        },
        {
            path: ['series', 0, 'detail', 'offsetCenter'],
            value: [0, 0],
        },
        {
            path: ['series', 0, 'detail', 'rich', 'a'],
            value: {
                fontSize: 25,
                fontWeight: 700,
                width: 30,
                color: '#102E4A',
                align: 'center',
            },
        },
        {
            path: ['series', 0, 'pointer', 'show'],
            value: false,
        },
        ])
    }

    return { layoutEdit: layoutEdits }
}

const chartRenderSettings = (widget, dashStore, xAxisLabels, fData) => {
    const { chartType } = widget

    const renderFunc = {
        pie: pieRenderSettings,
        donut: pieRenderSettings,
        column: columnRenderSettings,
        bar: columnRenderSettings,
        stackedColumn: columnRenderSettings,
        stackedBar: columnRenderSettings,
        line: columnRenderSettings,
        radar: radarRenderSettings,
        treemap: treemapRenderSettings,
        quadrantAnalysis: quadrantAnalysisRenderSettings,
        gauge: gaugeRenderSettings,
        'gauge0-5': gaugeRenderSettings,
    }[chartType]

    return renderFunc ? renderFunc(widget, dashStore, xAxisLabels, fData) : { layoutEdit: [] }
}

export default chartRenderSettings
